/**
 * A video in place of a photograph.
 *
 * The design draws a play button over the meetings tiles and the hero band
 * (`1225:5741` inside `6377:86988`) and there was nothing behind it - the model held
 * images only. A `videoUrl` on `shared.media` or `shared.card` turns that frame into a
 * player.
 *
 * **Nothing from YouTube or Vimeo loads until the visitor clicks.** What is rendered
 * first is a facade: the poster image, the play button, and a real `<button>`. Only the
 * click inserts the iframe. That is not only lighter - embedding YouTube on page load
 * sets cookies and calls Google before anyone has agreed to anything, which is the one
 * thing `lib/consent.ts` exists to prevent. The facade keeps the section honest without
 * making video conditional on consent: a visitor who presses play has asked for it.
 *
 * `youtube-nocookie.com` is used for the same reason, and `rel=0` keeps the end screen
 * from advertising other channels.
 *
 * An address that is neither YouTube nor Vimeo renders as a plain link over the poster
 * rather than an empty frame, so a Wistia or self-hosted URL degrades instead of
 * breaking.
 */
import { useState } from 'react';
import { useSite } from '../../lib/site-context';
import { Icon } from './Icon';
import { Image, SIZES } from './Image';
import type { StrapiMedia } from '../../types/sections';

/** `null` when the address is not one we can embed. */
export function embedUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, '');

  if (host === 'youtu.be') {
    const id = parsed.pathname.slice(1);
    return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : null;
  }

  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    // /watch?v=…, /embed/…, /shorts/… and /live/… all name the id differently.
    const id = parsed.searchParams.get('v')
      ?? parsed.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1];
    return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : null;
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = parsed.pathname.match(/(\d+)/)?.[1];
    return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
  }

  return null;
}

export function VideoEmbed({
  url, poster, alt, title, sizes = SIZES.half, playSize = 64, className = ''
}: {
  url: string;
  poster?: StrapiMedia | null;
  alt?: string;
  /** Names the player for a screen reader, and labels the play button. */
  title?: string;
  sizes?: string;
  playSize?: number;
  className?: string;
}) {
  const { t } = useSite();
  const [playing, setPlaying] = useState(false);
  const embed = embedUrl(url);
  const label = title ? `${t('video.play')}: ${title}` : t('video.play');

  if (playing && embed) {
    return (
      <iframe
        src={embed}
        title={title ?? 'Video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className={`h-full w-full border-0 bg-black ${className}`}
      />
    );
  }

  const face = (
    <>
      {poster ? (
        <Image
          media={poster}
          alt={alt ?? ''}
          sizes={sizes}
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
      ) : null}
      {/* The design's button: a glass square with a ring-and-triangle glyph inside. */}
      <span className="relative grid place-items-center rounded-m border border-white/25 bg-black/40 p-5 text-white backdrop-blur-[7px] transition group-hover:bg-black/60">
        <Icon name="play-circle" size={playSize} />
      </span>
    </>
  );

  const shell = `group relative grid h-full w-full place-items-center overflow-hidden bg-grey-700 ${className}`;

  // Without an embeddable address the poster still works as a link out to wherever the
  // video actually lives.
  return embed ? (
    <button type="button" onClick={() => setPlaying(true)} aria-label={label} className={shell}>
      {face}
    </button>
  ) : (
    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label} className={shell}>
      {face}
    </a>
  );
}
